import Pusher from "pusher-js";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import ChatServices from "../apis/chat/ChatServices";
import { useAuth } from "../context/UseContext";
import { baseUrl } from "../config/axios";

const PusherContext = createContext();

export const usePusher = () => useContext(PusherContext);

export const PusherProvider = ({ children }) => {

  const { user } = useAuth();

  const pusherRef = useRef(null);
  const [isPusherReady, setIsPusherReady] = useState(false);

  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);

  const getConversations = async () => {
    try {
      const { data } = await ChatServices.getConversations()
      setConversations(data)
    } catch (error) {
      console.log('🚀 => getConversations => error:', error)
    }
  }

  const initPusher = async () => {
    try {
      const pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY, {
        cluster: 'ap2',
        channelAuthorization: {
          endpoint: baseUrl + 'api/channelAuthorization',
          headers: { 'Authorization': `Bearer ${user?.token}` }
        }
      });

      pusherRef.current = pusher;
      setIsPusherReady(true);
    } catch (error) {
      console.log(' initPusher => error:', error)
    }
  };

  const updateConversation = useCallback((message) => {
    try {
      const isActive = messages.length > 0 && message?.conversation_id === messages[0]?.conversationId;

      setConversations((prev) => {
        const index = prev.findIndex(c => c?.id === message?.conversation_id);
        if (index === -1) {
          return prev;
        }

        const updatedConv = {
          ...prev[index],
          un_readCount: isActive ? 0 : (prev[index].un_readCount ?? 0) + 1,
          last_message: message
        };

        const newConversationList = [...prev];
        newConversationList.splice(index, 1);
        return [updatedConv, ...newConversationList];

      });
    } catch (error) {
      console.log(' updateMessages => error:', error)
    }
  }, [messages]);

  const handleNewMessage = useCallback((data) => {
    const newMsg = {
      conversationId: data?.conversation_id,
      date: data?.created_at,
      message: data?.message,
      attachment: data?.attachment,
      userId: data?.user_id,
      id: data?.id,
      seen: data?.seen ?? 0,
    };

    setMessages((prev) => {
      if (prev.length === 0 || data?.conversation_id === prev[0]?.conversationId) {
        return [...prev, newMsg];
      }
      return prev;
    });

    updateConversation(data);
  }, [updateConversation]);

  useEffect(() => {
    const ready = user && !!user.id;
    if (!ready) {
      return;
    }

    if (user) {
      initPusher();
      getConversations();
    }
  }, [user]);

  useEffect(() => {
    if (!isPusherReady || !user?.id || !pusherRef.current) {
      return;
    }

    const messageChannel = pusherRef.current.subscribe('private-messages-channel.' + user.id);
    messageChannel.bind('message.created', (data) => {
      const { message } = data
      handleNewMessage(message)
    });

    return () => {
      if (messageChannel && pusherRef.current) {
        pusherRef.current.unsubscribe('private-messages-channel.' + user?.id);
        pusherRef.current.disconnect();
      }
    };
  }, [isPusherReady, user]);

  return (
    <PusherContext.Provider
      value={{
        pusherRef: pusherRef.current,
        conversations,
        setConversations,
        messages,
        setMessages,
        handleNewMessage
      }}
    >
      {children}
    </PusherContext.Provider>
  );
};